import type { ReactElement, ReactNode } from 'react'

/**
 * Props for {@link AppHeader}.
 *
 * @property title - The plane/page title, rendered as a heading.
 * @property planeSwitcher - The plane-switch control slot (e.g. a
 *   `PlaneSwitcherMenu`); omitted when the viewer has one plane.
 * @property actions - Page-level action slot (buttons, etc.).
 * @property userMenu - The account-menu slot (e.g. a `UserMenu`).
 */
export interface AppHeaderProps {
	title: string
	planeSwitcher?: ReactNode
	actions?: ReactNode
	userMenu?: ReactNode
}

/**
 * The plane header: a title with a plane-switch slot, an actions slot and an
 * account-menu slot.
 *
 * @remarks
 * Presentational and routing-free — every region arrives as a prop. Renders a
 * `<header>` landmark; the title is the sole `<h1>`. Every class is a `ds-*`
 * token utility.
 */
export function AppHeader({ title, planeSwitcher, actions, userMenu }: AppHeaderProps): ReactElement {
	return (
		<header className="flex items-center gap-ds-4 border-b border-ds-border bg-ds-surface px-ds-gutter py-ds-3">
			{planeSwitcher ? <div>{planeSwitcher}</div> : null}
			<h1 className="text-ds-heading-3 text-ds-text">{title}</h1>
			<div className="ml-auto flex items-center gap-ds-3">
				{actions ? <div>{actions}</div> : null}
				{userMenu ? <div>{userMenu}</div> : null}
			</div>
		</header>
	)
}
